<template>
  <div>
    <div class="dependencies-header mb-5">
      <div>
        <h3 class="title is-5 mb-2">Dependencias de Colaboradores</h3>
        <p class="has-text-grey is-size-7">
          Crea campos personalizados para clasificar y organizar a tus
          colaboradores
        </p>
      </div>
      <b-button
        v-if="!isAddingDependency"
        type="is-primary"
        icon-left="plus"
        @click="isAddingDependency = true"
      >
        Nueva Dependencia
      </b-button>
    </div>

    <!-- Dependency Form -->
    <div v-if="isAddingDependency" class="dependency-form mb-5">
      <h4 class="has-text-weight-semibold mb-4">
        {{ form._id ? "Editar Dependencia" : "Nueva Dependencia" }}
      </h4>

      <b-field
        label="Nombre de la Dependencia"
        :type="nameError ? 'is-danger' : null"
        :message="nameError"
      >
        <b-input
          v-model="form.name"
          placeholder="Ej: Departamento, Rol, Edad, Género"
          @input="touched.name = true"
          @blur="touched.name = true"
        />
      </b-field>

      <b-field label="Tipo de Campo">
        <b-select v-model="form.type" expanded>
          <option value="text">Texto</option>
          <option value="number">Número</option>
          <option value="select">Selección (Lista)</option>
        </b-select>
      </b-field>

      <b-field
        v-if="form.type === 'select'"
        label="Opciones (separadas por comas)"
        :type="nameError ? 'is-danger' : null"
        :message="nameError ? nameError : 'Dar enter para agregar una nueva'"
      >
        <b-taginput
          v-model="form.options"
          ellipsis
          icon="label"
          placeholder="Ej: Ventas, Marketing, IT, RRHH"
          aria-close-label="Delete this tag"
          @input="touched.name = true"
          @blur="touched.name = true"
        >
        </b-taginput>
      </b-field>

      <div class="buttons">
        <b-button
          type="is-primary"
          icon-left="content-save"
          @click="handleSubmit()"
          :disabled="!isFormValid"
        >
          {{ form._id ? "Actualizar" : "Guardar" }}
        </b-button>
        <b-button type="is-light" @click="handleCancelForm">
          Cancelar
        </b-button>
      </div>
    </div>

    <!-- Dependencies List -->
    <div class="dependencies-list">
      <div v-if="dependencies.length === 0" class="empty-state">
        <b-icon
          icon="folder-tree"
          size="is-large"
          custom-class="has-text-grey-light mb-3"
        />
        <p class="has-text-grey">No hay dependencias creadas</p>
        <p class="has-text-grey is-size-7">
          Crea tu primera dependencia para organizar a tus colaboradores
        </p>
      </div>

      <div
        v-for="dep in dependencies"
        v-else
        :key="dep._id"
        class="dependency-item"
      >
        <div class="dependency-content">
          <div class="dependency-info">
            <h4 class="has-text-weight-medium">
              {{ dep.name }}
            </h4>
            <b-tag size="is-small" class="ml-2">
              {{ getDependencyTypeLabel(dep.type) }}
            </b-tag>
          </div>
          <p v-if="dep.options" class="has-text-grey is-size-7 mt-1">
            Opciones: {{ dep.options.join(", ") }}
          </p>
        </div>
        <div class="dependency-actions">
          <b-button icon-left="pencil" @click="handleEditDependency(dep)" />
          <b-button
            type="is-danger"
            outlined
            icon-left="delete"
            @click="handleDeleteDependency(dep._id)"
          />
        </div>
      </div>
    </div>

    <!-- Info Message -->
    <b-message type="is-info" class="mt-5">
      <h4 class="has-text-weight-semibold mb-2">
        Sobre las Dependencias
      </h4>
      <ul class="instruction-list">
        <li>
          • Las dependencias son campos personalizados para clasificar
          colaboradores
        </li>
        <li>
          • Puedes crear campos de texto, número o listas de selección
        </li>
        <li>
          • Estos campos estarán disponibles al agregar o editar colaboradores
        </li>
        <li>
          • Usa dependencias para filtrar y organizar mejor tu equipo
        </li>
      </ul>
    </b-message>
  </div>
</template>
<script>
import { mapState } from "vuex";
import store from "../../store";
import {
  createDependences,
  deleteDependence,
  fetchDependencies,
  updateDependences,
} from "../../api";

export default {
  name: "Dependencies",
  components: {},
  data() {
    return {
      dependencies: [],
      isAddingDependency: false,
      // Form
      form: {
        _id: null,
        name: "",
        type: "text",
        options: [],
      },
      touched: {
        name: false,
        email: false,
        options: false,
      },
      editForm: {},
    };
  },
  computed: {
    ...mapState(["idtoken"]),
    currentProcess() {
      return store.state.selectedProcess;
    },
    // Validate Form
    nameError() {
      if (!this.touched.name) return null;
      if (!this.form.name) return "Campo obligatorio";
      return null;
    },
    typeError() {
      if (!this.form.type) return "Campo obligatorio";
      return null;
    },
    optionsError() {
      if (this.form.type === "select") return null;
      if (!this.touched.options) return null;
      if (!this.form.options.length) return "Campo obligatorio";
      return null;
    },
    isFormValid() {
      const noErrors = !this.nameError && !this.typeError & !this.optionsError;
      const allFilled =
        this.form.name.trim() !== "" &&
        this.form.type.trim() !== "" &&
        (this.type === "select" ? this.form.options.length : true) &&
        (this.form._id ? !this.isEqualObjects(this.editForm, this.form) : true);
      return noErrors && allFilled;
    },
  },
  created() {
    if (this.idtoken) {
      this.getDependecies();
    }
  },
  methods: {
    async getDependecies() {
      return fetchDependencies(this.currentProcess._id).then(
        (res) => (this.dependencies = res)
      );
    },
    // Actions
    handleSubmit() {
      if (!this.isFormValid) return;

      if (this.form._id) {
        updateDependences({
          ...this.form,
          options: this.form.options.length ? this.form.options : null,
        })
          .then((res) => {
            this.getDependecies().then(() => {
              this.$buefy.toast.open({
                message: "Dependencia actualizada exitosamente",
                type: "is-success",
              });
            });
          })
          .catch((err) => {
            console.log(err);
            this.$buefy.toast.open({
              message: "Error al actualizar la dependencia",
              type: "is-danger",
            });
          })
          .finally(() => {
            this.clearForm();
            this.isAddingDependency = false;
            this.editForm = {};
          });
        return;
      }

      createDependences({
        idProcess: this.currentProcess._id,
        ...this.form,
        options: this.form.options.length ? this.form.options : null,
      })
        .then(() => {
          this.getDependecies().then(() => {
            this.$buefy.toast.open({
              message: "Dependencia agregada exitosamente",
              type: "is-success",
            });
          });
        })
        .catch((err) => {
          console.log(err);
          this.$buefy.toast.open({
            message: "Error al agregar la dependencia",
            type: "is-danger",
          });
        })
        .finally(() => {
          this.clearForm();
          this.isAddingDependency = false;
        });
    },
    handleEditDependency(dependency) {
      if (dependency) {
        const data = {
          _id: dependency._id,
          name: dependency.name,
          type: dependency.type,
          options: dependency.options ? dependency.options : [],
        };

        this.editForm = { ...data };
        this.form = { ...data };
        this.isAddingDependency = true;
      }
    },
    async handleDeleteDependency(id) {
      const config = {
        title: "¿Estás seguro de eliminarla?",
        text: "Se eliminará y no podra ser recuperada.",
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#de5b5b",
        confirmButtonText: "Sí, eliminar",
      };
      await this.$swal(config).then(async (result) => {
        if (result.isConfirmed) {
          deleteDependence(id)
            .then((res) => {
              this.getDependecies().then(() => {
                this.$buefy.toast.open({
                  message: "Dependencia eliminada exitosamente",
                  type: "is-success",
                });
              });
            })
            .catch((err) => {
              console.log(err);
              this.$buefy.toast.open({
                message: "Error al eliminar la dependencia",
                type: "is-danger",
              });
            });
        }
      });
    },
    handleCancelForm() {
      this.clearForm();
      this.isAddingDependency = false;
      this.editForm = {};
    },
    // Helpers
    clearForm() {
      this.form = {
        _id: null,
        name: "",
        type: "text",
        options: "",
      };
      this.touched = {
        name: false,
        email: false,
        options: false,
      };
    },
    isEqualObjects(obj1, obj2) {
      return (
        JSON.stringify(obj1, Object.keys(obj1).sort()) ===
        JSON.stringify(obj2, Object.keys(obj2).sort())
      );
    },
    getDependencyTypeLabel(type) {
      const labels = {
        text: "Texto",
        number: "Número",
        select: "Selección",
      };
      return labels[type] || type;
    },
  },
  watch: {
    idtoken(nv) {
      if (nv) {
        this.getDependecies();
      }
    },
    form: {
      handler(nv) {
        if (nv.type !== "select" && this.touched.options) {
          this.touched.options = false;
        }
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
/* Dependencies */
.dependencies-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.dependency-form {
  background-color: rgba(245, 245, 245, 0.8);
  border-radius: 6px;
  padding: 1.5rem;
}

.dependencies-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dependency-item {
  background-color: white;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  transition: background-color 0.2s;
}

.dependency-item:hover {
  background-color: rgba(245, 245, 245, 0.5);
}

.dependency-content {
  flex: 1;
}

.dependency-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dependency-actions {
  display: flex;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
}

.instruction-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.instruction-list li {
  font-size: 0.875rem;
  line-height: 1.6;
}

/* Responsive */
@media screen and (max-width: 1023px) {
  .dependencies-header {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/configuration/Dependencies.vue